import {
  AuditLogsResponse,
  List,
  ListFormUsagesResponse,
  ListPayload,
  ListsParams,
  ListsResponse,
  newListType,
  newObjectType,
} from '../types/list';
import { client } from './index';

// --- New React Lists API ---

const getListsReact = async (params: ListsParams = {}) => {
  const response = await client.get<ListsResponse>('/react_get_lists', { params });
  return response.data;
};

const updateList = async (id: number, data: ListPayload) => {
  const response = await client.put(`/react_update_list/${id}`, data);
  return response.data;
};

const deleteLists = async (ids: number[]) => {
  const response = await client.delete('/react_delete_lists', { params: { ids } });
  return response.data;
};

const duplicateList = async (id: number) => {
  const response = await client.post(`/react_duplicate_list/${id}`);
  return response.data;
};

const getListFormUsages = async (id: number) => {
  const response = await client.get<ListFormUsagesResponse>(`/react_list_forms/${id}`);
  return response.data;
};

const importList = async (file: File, listId?: number, listNames?: Record<string, string>) => {
  const formData = new FormData();
  formData.append('file', file);
  if (listId) formData.append('list_id', listId.toString());
  if (listNames && Object.keys(listNames).length > 0) {
    formData.append('list_names', JSON.stringify(listNames));
  }

  const response = await client.post<{ message: string }>('/react_import_list', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      Accept: 'application/json',
    },
  });
  return response.data;
};

const exportList = async (id: number) => {
  const response = await client.post<{ message: string }>(`/react_export_list/${id}`);
  return response.data;
};

const exportLists = async (ids: number[]) => {
  const response = await client.post<{ message: string; count: number }>('/react_export_lists', { ids });
  return response.data;
};

const createListReact = async (data: ListPayload) => {
  const response = await client.post<{ list_id: number }>('/react_create_list', { new_list: data });
  return response.data;
};

const getListById = async (id: number) => {
  const response = await client.get<{ userList: List | null }>('/react_get_objects', {
    params: { id, page_size: 1 },
  });
  return response.data?.userList ?? null;
};

export interface ListObjectsParams {
  list_id: number;
  search?: string;
  page?: number;
  page_size?: number;
}

const getListObjects = async (params: ListObjectsParams) => {
  const { list_id, ...rest } = params;
  const response = await client.get('/react_get_objects', {
    params: { id: list_id, ...rest },
  });
  return response.data;
};

const createListObject = async (listId: number, data: { name: string; description?: string; attribute1?: string; attribute2?: string; code?: string }) => {
  const response = await client.post('/react_create_object', {
    list_object: data,
    list_id: listId.toString(),
  });
  return response.data;
};

const updateListObject = async (id: number, data: { name?: string; description?: string; attribute1?: string; attribute2?: string; code?: string }) => {
  const response = await client.put(`/react_update_object/${id}`, {
    list_object: data,
  });
  return response.data;
};

const deleteListObject = async (id: number) => {
  const response = await client.delete(`/react_delete_object/${id}`);
  return response.data;
};

const deleteListObjects = async (ids: number[]) => {
  const response = await client.delete<{ deleted_count: number }>('/react_delete_objects', { params: { ids } });
  return response.data;
};

export interface ReorderItemPayload {
  id: number;
  position: number;
}

const reorderListObjects = async (listId: number, items: ReorderItemPayload[]) => {
  const response = await client.post<{ message?: string }>('/react_reorder_objects', {
    list_id: listId,
    items,
  });
  return response.data;
};

/**
 * Gives a list an explicit order without moving anything: the backend numbers the
 * items in the order they are already shown. What it enables is dragging.
 */
const orderList = async (listId: number) => {
  const response = await client.post<{ message?: string; ordered_count?: number }>(
    '/react_order_list',
    { list_id: listId },
  );
  return response.data;
};

const getAuditLogs = async (params: { page?: number; page_size?: number } = {}) => {
  const response = await client.get<AuditLogsResponse>('/import_logs', { params });
  return response.data;
};

export const listsApi = {
  getListsReact,
  createListReact,
  getListById,
  updateList,
  deleteLists,
  duplicateList,
  getListFormUsages,
  importList,
  exportList,
  exportLists,
  getListObjects,
  createListObject,
  updateListObject,
  deleteListObject,
  deleteListObjects,
  reorderListObjects,
  orderList,
  getAuditLogs,
};

// --- Legacy API (kept for backwards compatibility) ---

const getLists = async () => {
  const response = await client.get('/react_get_lists');
  const { data } = response;
  return data;
};

const createList = async (newList: newListType) => {
  const response = await client.post(
    '/react_create_list',
    {
      new_list: newList,
    },
    {
      headers: {
        accept: 'application/json',
      },
    },
  );
  const { data } = response;
  return data;
};

const getObjects = async (id?: string, metadataCode?: string, page?: number) => {
  const params = new URLSearchParams();
  if (metadataCode) params.append('metadata_code', metadataCode);
  if (id) params.append('id', id);
  if (page) params.append('page', page.toString());
  params.append('page_size', '50');

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/${
    metadataCode === 'assets' ? 'company_assets' : 'react_get_objects'
  }${queryString}`;

  const response = await client.get(url);
  const { data } = response;
  return data;
};

const getObjectsListByMetadataCode = async (metadataCode: string) => {
  const params = new URLSearchParams();
  params.append('metadata_code', metadataCode);

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `/react_get_objects${queryString}`;

  const response = await client.get(url);
  const { data } = response;
  return data;
};

const createObject = async (listId: string, list_object: newObjectType) => {
  const response = await client.post('/react_create_object', {
    list_object,
    list_id: listId,
  });
  const { data } = response;
  return data;
};

const updateObject = async (id: string, list_object: newObjectType) => {
  const response = await client.put(`/react_update_object/${id}`, {
    list_object,
  });
  const { data } = response;
  return data;
};

const deleteObject = async (id: string) => {
  const response = await client.delete(`/react_delete_object/${id}`);
  const { data } = response;
  return data;
};

export const listObjectsApi = {
  getObjects,
  createObject,
  updateObject,
  deleteObject,
  getObjectsListByMetadataCode,
};

export const userListsApi = { getLists, createList };
